// src/oura/newscan/copy.ts
//
// Every word the New scan frame and its shared pieces put on screen, in one
// place so the four steps read in one voice. Sentence case, numerals, `·` as
// the separator, no em dashes, no "please". Step builders add their own step
// copy here or in their step file; the frame never hard-codes a string.

import type { BuyMode, DecisionSeverity, ScanDraftKind, ScanDraftPurpose } from "@service/aeo/scanDraftTypes";

/** "1 decision", "2 decisions". `many` defaults to `one` + "s". */
export function plural(n: number, one: string, many = `${one}s`): string {
    return `${n} ${n === 1 ? one : many}`;
}

/** 2400 -> "2.4k", 1000 -> "1k", 880 -> "880". For chips only; full numbers go in tooltips. */
export function shortCount(n: number): string {
    if (n < 1000) return String(n);
    const k = n / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1).replace(/\.0$/, "")}k`;
}

/** 2400 -> "2,400". */
export function fullCount(n: number): string {
    return n.toLocaleString("en-US");
}

/**
 * A refused write in plain words. The server's lint sentence names variables
 * in braces ("question 39: unfilled variable(s) {alternative_category}"); staff
 * read the word's label instead, and what to do: "Not saved. Question 39 also
 * needs what else they consider. Fill that word first and this one saves."
 */
export function plainRefusal(message: string, labelOf: (variable: string) => string | null | undefined): string {
    const name = (v: string) => (labelOf(v) || v.replace(/_/g, " ")).toLowerCase();
    const body = message.replace(/^That change would break a check:\s*/i, "");
    const blank = /question (\d+): unfilled variable\(s\) \{([a-z0-9_]+)\}/i.exec(body);
    if (blank) return `Not saved. Question ${blank[1]} also needs ${name(blank[2])}. Fill that word first and this one saves.`;
    const words = body
        .replace(/\{([a-z0-9_]+)\}/gi, (_, v: string) => `“${name(v)}”`)
        .replace(/:?\s*exact duplicate of q(\d+)/i, " would repeat question $1")
        .replace(/:?\s*near[- ]duplicate of q(\d+)/i, " would be too close to question $1")
        .replace(/\bq(\d+)\b/g, "question $1");
    return body === message ? words : `Not saved: ${words.charAt(0).toLowerCase()}${words.slice(1)}`;
}

export const FLOW_TITLE: Record<ScanDraftKind, string> = {
    new_scan: "New scan",
    edit_set: "Edit the questions",
    recipe_change: "Recipe update",
};

/** The top bar's title: a signed client's First AI check keeps its own name (JOURNEY-SPEC §2.5). */
export function flowTitle(kind: ScanDraftKind, purpose?: ScanDraftPurpose | null): string {
    return purpose === "first_check" ? FIRST_CHECK.title : FLOW_TITLE[kind];
}

/** Words the flow says only on a signed client's First AI check (JOURNEY-SPEC §2.5). */
export const FIRST_CHECK = {
    title: "First AI check",
    /** Close with the draft still open: the onboarding page's Next card picks it up again. */
    closeSaved: "Draft saved. Pick it up from the onboarding page.",
    backToOnboarding: "Back to onboarding",
    closedLine: "Open the onboarding page to draft it again.",
    /** The landing screen's notice once it starts. */
    started: "The first AI check has started.",
} as const;

export const STEP_LABEL = {
    website: "Website",
    business: "Business",
    review: "Review",
    start: "Start",
} as const;

export const FRAME = {
    close: "Close",
    back: "Back",
    stepTrack: "Steps",
    stepDone: "done",
    stepNeedsLook: "not checked yet",
    stepCurrent: "current step",
    stepLocked: "not available yet",
    /** Said when the step changes: "Step 2 of 4: Business". */
    stepAnnounce: (n: number, of: number, label: string) => `Step ${n} of ${of}: ${label}`,
    /** The snackbar on Close when a draft exists: a new business's draft is listed under New scan. */
    closeSaved: "Draft saved. Find it under New scan.",
    /** The same for a business's draft: its page offers Resume. */
    closeSavedBusiness: "Draft saved. Continue it from this page.",
    loading: "Loading the draft",
    retry: "Try again",
    network: "Check your connection and try again.",
    absentTitle: "The new scan is not available here yet.",
    absentLine: "Staff run new scans from the businesses list until it is.",
    absentBack: "Back to businesses",
    noBusiness: "This business could not be opened.",
    openingDraft: "Opening the draft",
    readFirst: "Read the site first.",
    draftMissing: "This draft could not be opened. Close it and start again from the business.",
    /** A discarded or superseded draft reopened by URL or Back. */
    closedTitle: "This draft was closed.",
    closedLineBusiness: "Start a new one from the business.",
    closedLineNew: "Start a new one from the businesses list.",
    closedBackBusiness: "Back to the business",
    /** The business already has an open draft of the other kind; it opens instead. Keyed by the open draft's kind. */
    otherKindOpen: {
        edit_set: "An edit of these questions is already open. Finish or discard it before the recipe update.",
        recipe_change: "A recipe update is already open. Finish or discard it before editing the questions.",
        new_scan: "A new scan for this business is already open. Finish or discard it first.",
    } satisfies Record<ScanDraftKind, string>,
    gscBadLink: "The Google sign-in link was not recognised.",
} as const;

export const FOOT = {
    cancel: "Cancel",
    /** The confirm row's verb when the step gives none. */
    confirmVerb: "Confirm",
} as const;

export const TAG_INPUT = {
    added: (v: string) => `Added ${v}`,
    removed: (v: string) => `Removed ${v}`,
    duplicate: (v: string) => `${v} is already in the list`,
    remove: (v: string) => `Remove ${v}`,
    hint: "Enter or comma adds one. Backspace removes the last.",
    mark: "Added by you",
} as const;

export const RADIO_SEG = {
    suggested: "Suggested",
} as const;

export const BUY_LABEL: Record<BuyMode, string> = {
    visit: "Visit us",
    online: "Online",
    both: "Both",
    we_go_to_them: "We go to them",
};

export const LANGUAGE_MODE_LABEL = {
    auto: "Auto",
    on: "On",
    off: "Off",
} as const;

export const DECISION = {
    /** sr-only words beside the icon square, so the severity is never colour alone. */
    severity: {
        blocker: "Must fix",
        decide: "To decide",
        suggestion: "Optional",
        info: "Note",
    } satisfies Record<DecisionSeverity, string>,
    /** Leads an option's wording preview, shown above its pill: "New: physio clinic near Bangsar". */
    previewLead: "New:",
    /** gsc_candidate evidence: "“physio bangsar”: 2,400 searches a year". */
    evidence: (query: string, searches: number) => `“${query}”: ${fullCount(searches)} searches a year`,
} as const;

export const OUTCOME = {
    /** "3 fixed." Decisions answered, shown before the last change's label. */
    fixed: (n: number) => `${n} fixed.`,
    last: "Last:",
    undo: "Undo",
    undoLabel: (label: string) => `Undo: ${label}`,
} as const;

/**
 * The progress list on the Website step while the read runs. The same words
 * and order as the read's own `pages` (scanDraft.ts STANDARD_PAGES and
 * PAGE_LABELS, fixtures/samples.ts), so the list does not change when it lands.
 */
export const READ_PAGES = ["Home page", "Main pages", "Contact page", "About page"] as const;

/** A run of question text: plain words, or a `{word}` still waiting for its value. */
export type SlotPart = { text: string; variable: null } | { text: string; variable: string };

/**
 * Split question text where a blank word sits ("… around {price_point} in
 * KL?"), so the screen can draw each blank as a blank instead of its raw
 * token. Text with no blank comes back as one plain part.
 */
export function slotParts(text: string): SlotPart[] {
    const out: SlotPart[] = [];
    const slot = /\{([a-z0-9_]+)\}/g;
    let at = 0;
    let m = slot.exec(text);
    while (m) {
        if (m.index > at) out.push({ text: text.slice(at, m.index), variable: null });
        out.push({ text: m[0], variable: m[1] });
        at = m.index + m[0].length;
        m = slot.exec(text);
    }
    if (at < text.length || out.length === 0) out.push({ text: text.slice(at), variable: null });
    return out;
}

export const SLOT_TEXT = {
    blank: "…",
    /** sr-only beside a blank: "Price needed". */
    needed: (label: string) => `${label} needed`,
} as const;

/** The open New scan drafts, under the Website field and above the businesses list. */
export const OPEN_DRAFTS = {
    title: "Open drafts",
    started: (ago: string) => `Started ${ago}`,
    continue: "Continue",
    continueLabel: (name: string) => `Continue the draft for ${name}`,
} as const;
